import { Box, Button, FormControlLabel, Link, Stack, Switch, Tooltip, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router";
import { IsUnauthorizedError, useLogin } from "providers/Login";
import { useNotification } from "providers/Notification";
import DataTable, { GetSortString, RestoreColumnsState, SaveColumnsState, useTableColumnState } from "components/Table";
import StatusChip from "components/StatusChip";
import { fromUnix } from "helpers/dateFormat";

interface ServiceInfo {
    id: string
    instanceName: string
    kind: string
    role: string
    status: string
    version?: string
    mode?: string
    description: string
}

interface GetServicesResponse {
    services: ServiceInfo[]
    fetchedAt: number
}

type ColumnsType = "kind" | "instanceName" | "role" | "status" | "version" | "description";

const REFRESH_INTERVAL_MS = 15_000;
const COLUMNS_STATE_KEY = "servicesTablev1";

const KIND_LABEL: Record<string, string> = {
    "shield-all": "Shield (all-in-one)",
    "shield-web": "Shield Web",
    "shield-api": "Shield API",
    "shield-icap": "Shield ICAP",
    "shield-alert": "Shield Alert",
    "redis": "Settings",
    "elasticsearch": "Activity Log",
    "opensearch": "Activity Log",
};

const MODE_LABEL: Record<string, string> = {
    "all": "All",
    "web": "Web",
    "api": "API",
    "icap": "ICAP",
    "alert": "Alert",
};


export default function Services() {
    const navigate = useNavigate();
    const login = useLogin();
    const notification = useNotification();

    const [finishedLoading, setFinishedLoading] = useState(false);
    const [services, setServices] = useState<ServiceInfo[]>([]);
    const [fetchedAt, setFetchedAt] = useState<Date | null>(null);
    const [autoRefresh, setAutoRefresh] = useState(true);

    const [columnsState, setColumnsState] = useTableColumnState<ColumnsType>([
        { columnId: "kind", width: "18%" },
        { columnId: "instanceName", width: "20%" },
        { columnId: "role", width: "10%", sortOrder: 1, direction: "asc" },
        { columnId: "status", width: "12%" },
        { columnId: "version", width: "10%" },
        { columnId: "description", width: "30%" },
    ]);

    const columnsStateRef = useRef(columnsState);
    columnsStateRef.current = columnsState;

    const req = login.GetAxios();

    const loadData = async (c: typeof columnsState, showSpinner: boolean) => {
        if (!req) {
            return;
        }
        if (showSpinner) {
            setFinishedLoading(false);
        }
        try {
            const resp = await req.get<GetServicesResponse>("/api/services", {
                params: {
                    sortBy: GetSortString(c),
                },
            });
            setColumnsState(c);
            setServices(resp.data.services || []);
            setFetchedAt(resp.data.fetchedAt ? fromUnix(resp.data.fetchedAt) : new Date());
            setFinishedLoading(true);
        } catch (err) {
            if (IsUnauthorizedError(err)) {
                return; //GetAxios already dropped the session and redirected
            }
            notification.Display({
                title: "Error",
                message: "Could not fetch services status",
                type: "error",
            });
            setFinishedLoading(true);
        }
    };

    useEffect(() => {
        loadData(RestoreColumnsState(COLUMNS_STATE_KEY, columnsStateRef.current), true);
        const handle = window.setInterval(() => {
            loadData(columnsStateRef.current, false);
        }, REFRESH_INTERVAL_MS);
        return () => {
            window.clearInterval(handle);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoRefresh]);

    useEffect(() => {
        SaveColumnsState(COLUMNS_STATE_KEY, columnsState);
    }, [columnsState]);

    return <>
        <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={"10px"}>
            <Typography variant="h1">Services</Typography>
            <Stack direction="row" alignItems="center" gap={2}>
                {fetchedAt && (
                    <Typography variant="subtitle2" color="text.secondary">
                        Last updated {fetchedAt.toLocaleTimeString()}
                    </Typography>
                )}
                <FormControlLabel
                    control={
                        <Switch
                            checked={autoRefresh}
                            onChange={(e) => setAutoRefresh(e.target.checked)}
                            size="small"
                        />
                    }
                    label={<Typography variant="subtitle2">Auto-refresh</Typography>}
                />
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => loadData(columnsStateRef.current, true)}
                >
                    Refresh
                </Button>
            </Stack>
        </Box>

        <DataTable
            sx={{
                maxHeight: "85%",
            }}
            size="medium"
            columnsDataId={COLUMNS_STATE_KEY}
            data={services}
            dataCount={services.length}
            finishedLoading={finishedLoading}
            onSortChanged={(x) => loadData(x, false)}
            onColumnOrderChanged={(x) => setColumnsState(x)}
            columnsState={columnsState}
            columns={{
                kind: {
                    displayName: "Type",
                    sortable: true,
                    render: (x) => KIND_LABEL[x.kind] || x.kind,
                },
                instanceName: {
                    displayName: "Name",
                    sortable: true,
                    render: (x) => (
                        <Link
                            component="button"
                            underline="hover"
                            onClick={() => navigate(`/services/${encodeURIComponent(x.id)}`)}
                            sx={{ textAlign: "left" }}
                        >
                            {x.instanceName || x.id}
                        </Link>
                    ),
                },
                role: {
                    displayName: "Mode",
                    sortable: true,
                    render: (x) => {
                        if (x.mode) return MODE_LABEL[x.mode] ?? x.mode;
                        if (x.kind?.startsWith("shield-")) return MODE_LABEL[x.kind.split("-")[1]] ?? x.kind.split("-")[1];
                        return x.role || "—";
                    },
                },
                status: {
                    displayName: "Status",
                    sortable: true,
                    render: (x) => <StatusChip status={x.status} />,
                },
                version: {
                    displayName: "Version",
                    sortable: false,
                    render: (x) => x.version || "—",
                },
                description: {
                    displayName: "Description",
                    sortable: false,
                    render: (x) => (
                        <Tooltip title={x.description} placement="top-start">
                            <span>{x.description}</span>
                        </Tooltip>
                    ),
                },
            }}
        />
    </>;
}
